import angular from "angular";
import * as XLSX from "xlsx";

import {
  Calculation,
  CalculationListItem,
  PayrollDetails,
  UserDefinedRow,
  Employments,
  Years,
  EnumerationsLogic,
  OData,
  Language,
  Avatar,
} from "@salaxy/core";

import { UiHelpers, SessionService } from "../../services";

import { OpenAI } from "openai";

/**
 * AI payroll assistant functionality.
 */
export class Payroll4AssistantController implements angular.IController {

  /**
   * For NG-dependency injection
   * @ignore
   */
  public static $inject = ["$timeout", "Employments", "UiHelpers", "SessionService"];

  /**
   * The current payroll (PayrollDetails) to assist.
   */
  public current: PayrollDetails;

  /** Current page */
  public page:  { current: number, total: number, items: CalculationListItem[], calcs: Calculation[], originalCalcs: Calculation[], calcRange: string, totalCalcCount: number} | undefined;

  /** Messages */
  public messages: any[] = [];

  /** Loading indicator */
  public loading: boolean = false;

  /** Current message */
  public content: string;

  private _openAiAvatar: Avatar = {
    "entityType": "company" as any,
    "firstName": "OpenAI",
    "lastName": null,
    "displayName": "OpenAI",
    "sortableName": "OpenAI",
    "pictureType": "uploaded" as any,
    "url": "https://cdn.salaxy.com/img/thumbnail/openai-3.png",
    "id": "OpenAI",
  };
  private openai: OpenAI;


  private key = "";
  constructor(private $timeout: angular.ITimeoutService, private employments: Employments, private uiHelpers: UiHelpers, private sessionService: SessionService) {
  }

  /**
   * Initializes the controller
   */
  public async $onInit() {
    /** Check assistant key */
    this.key = window.localStorage.getItem("openAiKey") || "";
    this.openai = new OpenAI({
      apiKey: this.key,
      dangerouslyAllowBrowser: true
    });
    await this.generateInitialMessages();
    this.$timeout(() => { });
  }

  /** Company avatar */
  public get myAvatar() {
    return this.sessionService.avatar;
  }

  /** Open AI avatar */
  public get openAiAvatar() {
    return this._openAiAvatar;
  }

  /** Chat */
  public async chatForPayroll() {
    // current payroll
    const payrollContent = "Tässä palkkalistassa tällä hetkellä olevat rivit" + "\r\n" + JSON.stringify(this.payrollToRows());
    this.messages.push({ "role": "system", "content": payrollContent , "hidden": true });
     this.messages.push({ "role": "system", "content": "Kun sinua pyydetään lisäämään palkkalistalle rivejä, palauta aina myös kaikki alkuperäiset palkkalistan rivit." , "hidden": true });

    this.messages.push({ "role": "user", "content": this.content ?? "", "hidden": false });
    this.content = "";
    const objDiv = document.getElementById("openairesponse");
    objDiv.scrollTop = objDiv.scrollHeight;
    this.$timeout(() => { });

    try {
      this.loading = true;
      const openaiStream = this.openai.beta.chat.completions.stream({
        model: "gpt-4o-mini",
        messages: this.messages,
        temperature: 0,
        response_format: {
          type: "json_schema",
          json_schema: {
            name: "Rows",
            strict: false,
            schema: {
              type: "object",
              properties: {
                "rows": {
                  "description": "Set of rows that make up the salary calculation:\r\nExpenses, Benefits, deductions etc. Note that side costs are not rows.",
                  "type": "array",
                  "items": {
                    "$ref": "#/definitions/UserDefinedRowExt"
                  }
                },
                definitions: {
                  "UserDefinedRowExt": {
                    "description": "This is a user defined calculation row ",
                    "type": "object",
                    "properties": {
                      "employmentId": {
                        "description": "ID of the employment relation of the worker.",
                        "type": "string"
                      },
                      "rowType": {
                        "$ref": "#/definitions/CalculationRowType",
                        "description": "Logical type of the row"
                      },
                      "message": {
                        "description": "Description text of the row that is shown in reports. If null, will be set according to type.",
                        "type": "string"
                      },
                      "count": {
                        "format": "double",
                        "description": "Count for the row - default is one",
                        "type": "number"
                      },
                      "price": {
                        "format": "double",
                        "description": "Price for the row",
                        "type": "number"
                      },
                      "accounting": {
                        "$ref": "#/definitions/RowAccounting",
                        "description": "Accounting related data for the row."
                      },
                      "data": {
                        "description": "Usecase specific data",
                        "type": "object",
                        "additionalProperties": {
                          "type": "object"
                        }
                      }
                    }
                  },
                  "CalculationRowType": {
                    "description": "CalculationRowType enumeration",
                    "enum": [
                      "unknown",
                      "salary",
                      "hourlySalary",
                      "monthlySalary",
                      "totalWorkerPayment",
                      "totalEmployerPayment",
                      "compensation",
                      "overtime",
                      "tesWorktimeShortening",
                      "eveningAddition",
                      "nightimeAddition",
                      "saturdayAddition",
                      "sundayWork",
                      "otherAdditions",
                      "paidSickLeaveSalary",
                      "paidSickLeaveHourlySalary",
                      "paidSickLeaveMonthlySalary",
                      "trainingSalary",
                      "trainingHourlySalary",
                      "trainingMonthlySalary",
                      "accomodationBenefit",
                      "mealBenefit",
                      "phoneBenefit",
                      "carBenefit",
                      "bicycleBenefit",
                      "otherBenefit",
                      "holidayCompensation",
                      "holidayBonus",
                      "holidaySalary",
                      "dailyAllowance",
                      "dailyAllowanceHalf",
                      "mealCompensation",
                      "milageOwnCar",
                      "toolCompensation",
                      "expenses",
                      "milageDaily",
                      "milageOther",
                      "unionPayment",
                      "foreclosure",
                      "advance",
                      "foreclosureByPalkkaus",
                      "prepaidExpenses",
                      "otherDeductions",
                      "deductibleOfExerciseAndCultureBenefit",
                      "childCareSubsidy",
                      "chainsawReduction",
                      "nonProfitOrg",
                      "subsidisedCommute",
                      "irIncomeType",
                      "board",
                      "remuneration",
                      "otherCompensation",
                      "workingTimeCompensation",
                      "employmentTermination",
                      "hourlySalaryWithWorkingTimeCompensation",
                      "paidSickLeave",
                      "training",
                      "taxAtSource",
                      "taxWithholding",
                      "absencePeriod",
                      "serviceCharge",
                      "service",
                      "totals"
                    ],
                    "type": "string"
                  },
                  "RowAccounting": {
                    "description": "Provides row specific data for accounting (both cost and financial accounting).",
                    "type": "object",
                    "properties": {
                      "vatPercent": {
                        "format": "double",
                        "description": "Percent for VAT calculation expressed as decimal: 24% is 0.24.",
                        "type": "number"
                      },
                      "vatEntries": {
                        "description": "Total VAT amount splitted by VAT rates.\r\nPlease note, that if both {Palkkaus.Model.Calc.RowAccounting.VatPercent} and the {Palkkaus.Model.Calc.RowAccounting.VatEntries} with some entries given,\r\nthe {Palkkaus.Model.Calc.RowAccounting.VatEntries} are applied.\r\nPlease note that this is null by default.",
                        "type": "array",
                        "items": {
                          "$ref": "#/definitions/VatEntry"
                        }
                      },
                      "dimensions": {
                        "description": "Values for cost accounting dimensions",
                        "type": "array",
                        "items": {
                          "$ref": "#/definitions/CostAccountingDimension"
                        }
                      }
                    }
                  },
                  "VatEntry": {
                    "description": "For VAT breakdown of a receipt (ALV-erittely) represents a single VAT rate and the revenue and tax with that rate.",
                    "type": "object",
                    "properties": {
                      "tax": {
                        "format": "double",
                        "description": "Tax amount of specific VAT rate in euro.\r\nThis value is mandatory, if the collection of VatEntries contains more than one item.",
                        "type": "number"
                      },
                      "vatRate": {
                        "$ref": "#/definitions/VatRate",
                        "description": "Applied VAT rate. This is always mandatory."
                      },
                      "taxPercent": {
                        "format": "double",
                        "description": "Tax as percent of Net sales (0-1). Currently just informative, but may later be used for Custom non-standard VAT rates.",
                        "type": "number"
                      },
                      "gross": {
                        "format": "double",
                        "description": "Gross sales for specific VAT rate including the tax (VAT).\r\nThis value is mandatory, if the collection of VatEntries contains more than one item.",
                        "type": "number"
                      },
                      "net": {
                        "format": "double",
                        "description": "Net sales for specific VAT rate excluding the tax (VAT).\r\nThis value is mandatory, if the collection of VatEntries contains more than one item.",
                        "type": "number"
                      }
                    }
                  },
                  "VatRate": {
                    "description": "VatRate enumeration",
                    "enum": [
                      "undefined",
                      "none",
                      "vat0000",
                      "vat1000",
                      "vat1350",
                      "vat1400",
                      "vat2400",
                      "vat2550",
                      "other"
                    ],
                    "type": "string"
                  },
                  "CostAccountingDimension": {
                    "description": "Id of the cost accounting dimension with optional percentage for shared costs.",
                    "type": "object",
                    "properties": {
                      "id": {
                        "description": "Dimension Id.",
                        "type": "string"
                      },
                      "value": {
                        "description": "Cost accounting dimension value id.",
                        "type": "string"
                      },
                      "percent": {
                        "format": "percent",
                        "description": "Sharing percent for the dimension.",
                        "type": "number"
                      }
                    }
                  },
                }
              },
            }
          }
        }
      });

      const responseMessage = {
        "role": "assistant",
        "hidden": true,
        "content": ""
      };

      for await (const part of openaiStream) {
        const delta = part.choices[0].delta;
        const finishReason = part.choices[0].finish_reason;
        if (this.messages.indexOf(responseMessage) === -1) {
          this.messages.push(responseMessage);
          console.log("responseMessage", responseMessage);
        }

        if (delta.content) {
          responseMessage.content += delta.content;
          objDiv.scrollTop = objDiv.scrollHeight;
          this.$timeout(() => { });
        }


        if (finishReason) {
          // NO
        }
      }
      this.loading = false;

      await this.modifyPayroll(responseMessage.content);

    } catch (error) {
      this.loading = false;
      console.error("Error in stream start:", error);
    }

    objDiv.scrollTop = objDiv.scrollHeight;
    this.$timeout(() => { });
  }

  /** Chat */
  public async chatForAdvise() {    // current payroll
    const payrollContent = "Tässä palkkalistan kaikki laskelmat listana (JSON-formaatti). Rivit ovat rakenteessa: rows." + "\r\n" + JSON.stringify(this.page.calcs);
    this.messages.push({ "role": "system", "content": payrollContent , "hidden": true });
    this.messages.push({ "role": "user", "content": this.content ?? "", "hidden": false });
    this.content = "";
    const objDiv = document.getElementById("openairesponse");
    objDiv.scrollTop = objDiv.scrollHeight;
    this.$timeout(() => { });

    try {
      //this.loading = true;
      const openaiStream = await this.openai.chat.completions.create({
        model: "gpt-4o-mini",
        messages: this.messages,
        temperature: 0,
        stream: true,
      });

      const responseMessage = {
        "role": "assistant",
        "hidden": false,
        "content": ""
      };

      for await (const part of openaiStream) {
        const delta = part.choices[0].delta;
        const finishReason = part.choices[0].finish_reason;
        if (this.messages.indexOf(responseMessage) === -1) {
          this.messages.push(responseMessage);
        }

        if (delta.content) {
          responseMessage.content += delta.content;
          objDiv.scrollTop = objDiv.scrollHeight;
          this.$timeout(() => { });
        }


        if (finishReason) {
          // NO
        }
      }

      //this.loading = false;
    } catch (error) {
      //this.loading = false;
      console.error("Error in stream start:", error);
    }

    objDiv.scrollTop = objDiv.scrollHeight;
    this.$timeout(() => { });
  }

  /** Chat */
  public async chatForReport() {

    this.messages.push({ "role": "user", "content": this.content , "hidden": false });
    const payrollContent = "Tässä palkkalistan kaikki laskelmat listana (JSON-formaatti). Rivit ovat rakenteessa: rows." + "\r\n" + JSON.stringify(this.page.calcs);
    const htmlInstruction = "Muodosta vain HTML:ää oleva vastaus. HTML:n pitäisi sisältää vain perus-tagejä. Ei tyylejä.";
    this.messages.push({ "role": "user", "content": payrollContent + "\r\n" + htmlInstruction , "hidden": true });
    this.content = "";

    const objDiv = document.getElementById("openairesponse");
    objDiv.scrollTop = objDiv.scrollHeight;
    this.$timeout(() => { });

    try {
      this.loading = true;
      const openaiStream = await this.openai.chat.completions.create({
       model: "gpt-4o-mini",
        messages: this.messages,
        temperature: 0,
        stream: true,
      });

      const responseMessage = {
        "role": "assistant",
        "hidden": true,
        "content": "",
        "type": "html",
        "file": "report.html"
      };

      for await (const part of openaiStream) {
        const delta = part.choices[0].delta;
        const finishReason = part.choices[0].finish_reason;
        if (this.messages.indexOf(responseMessage) === -1) {
          this.messages.push(responseMessage);
        }

        if (delta.content) {
          responseMessage.content += delta.content;
          objDiv.scrollTop = objDiv.scrollHeight;
          this.$timeout(() => { });
        }

        if (finishReason) {
          // NO
        }
      }

      console.log("html for report", responseMessage.content);
      // parse content between ```html and ```
      const htmlContentMatch = responseMessage.content.match(/```html([\s\S]*?)```/);
      if (htmlContentMatch && htmlContentMatch[1]) {
        responseMessage.content = htmlContentMatch[1].trim();
        responseMessage.hidden = false;
      } else {
        //responseMessage.content = responseMessage.content;
        responseMessage.hidden = false;
      }

      this.loading = false;
    } catch (error) {
      this.loading = false;
      console.error("Error in stream start:", error);
    }

    objDiv.scrollTop = objDiv.scrollHeight;
    this.$timeout(() => { });
  }

  /** Shows the selected report */
  public showReport(message: { content: string}) {
    this.uiHelpers.showDialog("salaxy-components/modals/ui/Html.html", null, { html: message.content, title:"Raportti" }, null,"lg");
  }

  /**
   * Reads the xls file
   * @param file Selected file
   */
  public async readFile(file: File): Promise<void> {
    if (!file) {
      return Promise.resolve(null);
    }
    const workbook = XLSX.read(await file.arrayBuffer(), { type: "buffer" });
    const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
    const data = XLSX.utils.sheet_to_json(firstSheet);

    this.messages.push({ "role": "user", "content": JSON.stringify(data), "hidden": false, type:"file", "file": file.name });

    this.$timeout(() => { });
  }

  /** Modify payroll rows */
  private async modifyPayroll(content: string) {
    const rows = JSON.parse(content).rows;
    console.log(rows);
    if (this.page) {
      for(const calc of this.page.calcs) {
        calc.rows.splice(0, calc.rows.length);
      }
    }
    for (const row of rows) {
      if (this.page) {
        const calc = this.page.calcs.find((c) => c.worker.employmentId === row.employmentId);
        if (calc) {
          const userDefinedRow = row as UserDefinedRow;
          userDefinedRow.accounting ??= { dimensions: [], vatEntries: [] };
          userDefinedRow.accounting.dimensions ??= [];
          userDefinedRow.accounting.vatEntries ??= [];
          userDefinedRow.data ??= {};
          calc.rows.push(row);
        }
      }
    }
  }

  private async generateInitialMessages(): Promise<void> {
    // employment relations
    const employments = (await OData.getAllPages((o) => this.employments.getODataActive("all", o), {})).items;
    const employees ="Tässä ovat kaikki työntekijät listattuna (JSON):\r\n" + JSON.stringify(employments.map((e) => ({employmentId: e.id, displayName: e.otherPartyInfo?.avatar?.displayName})));
    this.messages.push({ "role": "system", "hidden": true, "content": employees });
    // calculation row types
    const rowTypes = "Tässä ovat kaikki rivityypit (CalculationRowType) listattuna (JSON):\r\n" + JSON.stringify( EnumerationsLogic.getEnumMetadata("CalculationRowType", Language.Fi).values.map((e) => ({ rowType: e.name, label: e.label,description: e.descr})));
    this.messages.push({ "role": "system", "hidden": true, "content": rowTypes });
    // yearly changing numbers
    const yearlyChangingNumbers = "Tässä ovat kaikki vuosittain muuttuvat numerot listattuna vuodelle 2025 (JSON):\r\n" + JSON.stringify(Years.getYearlyChangingNumbers(new Date()).sideCosts);
    this.messages.push({ "role": "system", "hidden": true, "content": yearlyChangingNumbers });
  }

  private payrollToRows() {
    const extRows = [];
    for (const calc of this.page.calcs) {
      for (const row of calc.rows) {
        extRows.push({ employmentId:  calc.worker.employmentId, ...row });
      }
    }
    return extRows;
  }
}
